import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { enqueueGestion } from '../lib/offlineQueue';
import { compressImage } from '../lib/imageCompress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { MapPin, Camera, Send, Loader2, Navigation, User, Phone, FileText, Search, Home } from 'lucide-react';

export default function NuevaGestionPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [tipoSearch, setTipoSearch] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsReportLoading, setGpsReportLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [compressing, setCompressing] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    tipo: '', tipo_otro: '',
    vecino_nombre: '', vecino_telefono: '',
    vecino_calle: '', vecino_numero: '', vecino_colonia: '',
    calle: '', numero: '', colonia: '',
    lat: null, lng: null,
    ubicacion_pendiente: false,
    descripcion: ''
  });

  useEffect(() => {
    api.get('/tipos-gestion').then(res => setTipos(res.data)).catch(console.error);
  }, [api]);

  const captureGPS = (target) => {
    const setLoaderFn = target === 'vecino' ? () => {} : setGpsReportLoading;
    setLoaderFn(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          if (target === 'report') {
            setForm(f => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude, ubicacion_pendiente: false }));
          }
          setLoaderFn(false);
          toast.success('Ubicacion GPS capturada');
        },
        () => {
          if (target === 'report') {
            setForm(f => ({ ...f, lat: 19.4326, lng: -99.1332, ubicacion_pendiente: false }));
          }
          setLoaderFn(false);
          toast.info('Usando ubicacion predeterminada (CDMX)');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };

  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setCompressing(true);
    try {
      const compressed = await Promise.all(files.map(f => compressImage(f, 1024, 0.8)));
      setPhotos(prev => [...prev, ...compressed].slice(0, 5));
      const totalOriginal = files.reduce((s, f) => s + f.size, 0);
      const totalCompressed = compressed.reduce((s, f) => s + f.size, 0);
      if (totalOriginal > totalCompressed) {
        const savedKb = Math.round((totalOriginal - totalCompressed) / 1024);
        if (savedKb > 20) toast.success(`Fotos optimizadas: ${savedKb} KB ahorrados`);
      }
    } finally {
      setCompressing(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!form.tipo || !form.vecino_nombre || !form.vecino_telefono) {
      toast.error('Completa los campos requeridos (tipo, nombre y telefono del vecino)');
      return;
    }
    setLoading(true);
    const payload = {
      ...form,
      ubicacion_pendiente: !form.lat || !form.lng
    };

    // Offline path: if no connection, queue and let the sync worker handle it later
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      try {
        await enqueueGestion(payload, photos);
        toast.success('Guardada offline. Se enviará cuando recuperes conexión.');
        navigate('/mis-gestiones');
      } catch (e) {
        toast.error('No se pudo guardar offline');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const res = await api.post('/gestiones', payload);
      const gestionId = res.data.id;

      for (const photo of photos) {
        const fd = new FormData();
        fd.append('file', photo);
        await api.post(`/gestiones/${gestionId}/foto`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Gestion levantada exitosamente');
      navigate(`/gestion/${gestionId}`);
    } catch (err) {
      // Network failures → queue for later sync
      if (!err.response || err.code === 'ERR_NETWORK') {
        try {
          await enqueueGestion(payload, photos);
          toast.success('Sin conexión: guardada para sincronizar después');
          navigate('/mis-gestiones');
          return;
        } catch (e) { /* fall through */ }
      }
      toast.error(err.response?.data?.detail || 'Error al crear gestion');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const filteredTipos = tipos.filter(t =>
    t.toLowerCase().includes(tipoSearch.toLowerCase())
  );

  return (
    <div className="max-w-2xl mx-auto" data-testid="nueva-gestion-page">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Nueva Gestion
        </h1>
        <p className="text-base text-[#52525B] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Levanta una nueva gestion urbana
        </p>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3, 4].map(s => (
          <button
            key={s}
            onClick={() => setStep(s)}
            data-testid={`step-indicator-${s}`}
            className={`flex-1 h-1.5 rounded-full transition-all duration-200 ${step >= s ? 'bg-[#0052FF]' : 'bg-[#E4E4E7]'}`}
          />
        ))}
      </div>
      <p className="text-xs text-[#52525B] mb-4">Paso {step} de 4</p>

      {/* Step 1: Datos del Vecino */}
      {step === 1 && (
        <Card className="bg-white border border-[#E4E4E7] rounded-md" data-testid="step-1">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A] flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              <User className="w-5 h-5 text-[#0052FF]" /> Datos del Vecino
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Nombre del vecino *</Label>
              <Input
                data-testid="vecino-nombre-input"
                className="h-12 rounded-md border-[#E4E4E7]"
                value={form.vecino_nombre}
                onChange={e => updateField('vecino_nombre', e.target.value)}
                placeholder="Nombre completo"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Telefono WhatsApp *</Label>
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#52525B] flex-shrink-0" />
                <Input
                  data-testid="vecino-telefono-input"
                  className="h-12 rounded-md border-[#E4E4E7]"
                  value={form.vecino_telefono}
                  onChange={e => updateField('vecino_telefono', e.target.value)}
                  placeholder="+52 55 1234 5678"
                />
              </div>
            </div>

            <div className="pt-2 border-t border-[#E4E4E7]">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-3 flex items-center gap-2">
                <Home className="w-3.5 h-3.5" /> Direccion del vecino
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label className="text-xs text-[#52525B]">Calle</Label>
                  <Input
                    data-testid="vecino-calle-input"
                    className="h-12 rounded-md border-[#E4E4E7]"
                    value={form.vecino_calle}
                    onChange={e => updateField('vecino_calle', e.target.value)}
                    placeholder="Calle del vecino"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-[#52525B]">Numero</Label>
                  <Input
                    data-testid="vecino-numero-input"
                    className="h-12 rounded-md border-[#E4E4E7]"
                    value={form.vecino_numero}
                    onChange={e => updateField('vecino_numero', e.target.value)}
                    placeholder="#"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label className="text-xs text-[#52525B]">Colonia</Label>
                <Input
                  data-testid="vecino-colonia-input"
                  className="h-12 rounded-md border-[#E4E4E7]"
                  value={form.vecino_colonia}
                  onChange={e => updateField('vecino_colonia', e.target.value)}
                  placeholder="Colonia del vecino"
                />
              </div>
            </div>

            <Button
              data-testid="step-1-next"
              onClick={() => setStep(2)}
              className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md font-bold"
              disabled={!form.vecino_nombre || !form.vecino_telefono}
            >
              Siguiente
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Tipo de Reporte y Ubicacion */}
      {step === 2 && (
        <Card className="bg-white border border-[#E4E4E7] rounded-md" data-testid="step-2">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A] flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              <FileText className="w-5 h-5 text-[#0052FF]" /> Tipo y Ubicacion del Reporte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Tipo search */}
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Tipo de gestion *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <Input
                  data-testid="tipo-search-input"
                  className="h-12 pl-10 rounded-md border-[#E4E4E7]"
                  value={tipoSearch}
                  onChange={e => setTipoSearch(e.target.value)}
                  placeholder="Buscar tipo de gestion..."
                />
              </div>
              <div className="grid grid-cols-1 gap-1.5 max-h-60 overflow-y-auto border border-[#E4E4E7] rounded-md p-2">
                {filteredTipos.map(t => (
                  <button
                    key={t}
                    data-testid={`tipo-option-${t.replace(/\s/g, '-').toLowerCase()}`}
                    onClick={() => { updateField('tipo', t); setTipoSearch(''); }}
                    className={`text-left px-3 py-2.5 rounded-md text-sm transition-all ${
                      form.tipo === t
                        ? 'bg-[#0052FF] text-white font-semibold'
                        : 'hover:bg-[#F4F4F5] text-[#0A0A0A]'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {form.tipo && (
                <p className="text-sm text-[#0052FF] font-semibold">Seleccionado: {form.tipo}</p>
              )}
            </div>

            {/* Otro - text box */}
            {form.tipo === 'Otro' && (
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Describe el tipo de gestion *</Label>
                <Textarea
                  data-testid="tipo-otro-input"
                  className="min-h-[80px] rounded-md border-[#E4E4E7]"
                  value={form.tipo_otro}
                  onChange={e => updateField('tipo_otro', e.target.value)}
                  placeholder="Describe el problema o solicitud..."
                />
              </div>
            )}

            {/* Ubicacion del reporte */}
            <div className="pt-3 border-t border-[#E4E4E7]">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-3 flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" /> Ubicacion del reporte
              </p>
              <p className="text-xs text-[#52525B] mb-3">
                Puedes capturar la ubicacion GPS ahora o dejarla pendiente y agregarla despues.
              </p>

              <div className="flex gap-2 mb-3">
                <Button
                  data-testid="capture-report-gps-btn"
                  variant="outline"
                  onClick={() => captureGPS('report')}
                  disabled={gpsReportLoading}
                  className="flex-1 h-12 border-[#E4E4E7] rounded-md"
                >
                  {gpsReportLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Navigation className="w-4 h-4 mr-2" />}
                  Capturar GPS
                </Button>
                <Button
                  data-testid="skip-gps-btn"
                  variant="outline"
                  onClick={() => updateField('ubicacion_pendiente', true)}
                  className={`flex-1 h-12 border-[#E4E4E7] rounded-md ${form.ubicacion_pendiente ? 'bg-[#F59E0B]/10 border-[#F59E0B] text-[#F59E0B]' : ''}`}
                >
                  Dejar pendiente
                </Button>
              </div>

              {form.lat && form.lng && (
                <div className="p-3 rounded-md bg-[#10B981]/5 border border-[#10B981]/20 mb-3">
                  <p className="text-xs text-[#10B981] font-semibold">GPS capturado: {form.lat.toFixed(6)}, {form.lng.toFixed(6)}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label className="text-xs text-[#52525B]">Calle del reporte</Label>
                  <Input
                    data-testid="reporte-calle-input"
                    className="h-12 rounded-md border-[#E4E4E7]"
                    value={form.calle}
                    onChange={e => updateField('calle', e.target.value)}
                    placeholder="Calle donde esta el problema"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-[#52525B]">Numero</Label>
                  <Input
                    data-testid="reporte-numero-input"
                    className="h-12 rounded-md border-[#E4E4E7]"
                    value={form.numero}
                    onChange={e => updateField('numero', e.target.value)}
                    placeholder="#"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <Label className="text-xs text-[#52525B]">Colonia del reporte</Label>
                <Input
                  data-testid="reporte-colonia-input"
                  className="h-12 rounded-md border-[#E4E4E7]"
                  value={form.colonia}
                  onChange={e => updateField('colonia', e.target.value)}
                  placeholder="Colonia donde esta el problema"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-12 rounded-md border-[#E4E4E7]">
                Atras
              </Button>
              <Button
                data-testid="step-2-next"
                onClick={() => setStep(3)}
                className="flex-1 h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md font-bold"
                disabled={!form.tipo || (form.tipo === 'Otro' && !form.tipo_otro)}
              >
                Siguiente
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Evidencia */}
      {step === 3 && (
        <Card className="bg-white border border-[#E4E4E7] rounded-md" data-testid="step-3">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A] flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              <Camera className="w-5 h-5 text-[#0052FF]" /> Evidencia Fotografica
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Fotos de evidencia</Label>
              <p className="text-xs text-[#52525B]">Puedes agregar fotos ahora o despues desde el detalle de la gestion.</p>
              <label
                data-testid="photo-upload-area"
                className={`flex flex-col items-center justify-center h-32 rounded-md border-2 border-dashed transition-colors bg-[#FAFAFA] ${compressing ? 'border-[#F59E0B] cursor-wait' : 'border-[#E4E4E7] hover:border-[#0052FF] cursor-pointer'}`}
              >
                {compressing ? (
                  <>
                    <Loader2 className="w-8 h-8 text-[#F59E0B] mb-2 animate-spin" />
                    <span className="text-sm text-[#F59E0B] font-medium">Optimizando fotos...</span>
                  </>
                ) : (
                  <>
                    <Camera className="w-8 h-8 text-[#52525B] mb-2" />
                    <span className="text-sm text-[#52525B]">Toca para capturar o seleccionar fotos</span>
                    <span className="text-[10px] text-[#A1A1AA] mt-1">Se comprimen a 1024px para ahorrar datos</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  disabled={compressing}
                  className="hidden"
                  onChange={handlePhotoChange}
                />
              </label>
              {photos.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {photos.map((p, i) => (
                    <div key={`${p.name}-${p.size}-${p.lastModified}`} className="relative w-20 h-20 rounded-md overflow-hidden border border-[#E4E4E7]">
                      <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                        className="absolute top-0.5 right-0.5 w-5 h-5 bg-[#EF4444] text-white rounded-full text-xs flex items-center justify-center"
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Descripcion adicional</Label>
              <Textarea
                data-testid="descripcion-input"
                className="min-h-[100px] rounded-md border-[#E4E4E7]"
                value={form.descripcion}
                onChange={e => updateField('descripcion', e.target.value)}
                placeholder="Detalles adicionales del problema..."
              />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1 h-12 rounded-md border-[#E4E4E7]">
                Atras
              </Button>
              <Button
                data-testid="step-3-next"
                onClick={() => setStep(4)}
                className="flex-1 h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md font-bold"
              >
                Ver Resumen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Resumen y Envio */}
      {step === 4 && (
        <Card className="bg-white border border-[#E4E4E7] rounded-md" data-testid="step-4">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight text-[#0A0A0A] flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              <Send className="w-5 h-5 text-[#0052FF]" /> Resumen de la Gestion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-md bg-[#FAFAFA] border border-[#E4E4E7] space-y-3">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Vecino</p>
                <p className="text-sm text-[#0A0A0A] font-semibold">{form.vecino_nombre}</p>
                <p className="text-sm text-[#52525B]">{form.vecino_telefono}</p>
                {form.vecino_calle && (
                  <p className="text-xs text-[#52525B]">Domicilio: {form.vecino_calle} {form.vecino_numero}, {form.vecino_colonia}</p>
                )}
              </div>
              <div className="border-t border-[#E4E4E7] pt-2">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Tipo de gestion</p>
                <p className="text-sm text-[#0A0A0A] font-semibold">{form.tipo}{form.tipo === 'Otro' ? `: ${form.tipo_otro}` : ''}</p>
              </div>
              <div className="border-t border-[#E4E4E7] pt-2">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Ubicacion del reporte</p>
                {form.calle ? (
                  <p className="text-sm text-[#0A0A0A]">{form.calle} {form.numero}, {form.colonia}</p>
                ) : (
                  <p className="text-sm text-[#52525B] italic">No especificada</p>
                )}
                {form.lat && form.lng ? (
                  <p className="text-xs text-[#10B981] font-mono mt-1">GPS: {form.lat.toFixed(4)}, {form.lng.toFixed(4)}</p>
                ) : (
                  <p className="text-xs text-[#F59E0B] mt-1 font-semibold">Ubicacion GPS pendiente</p>
                )}
              </div>
              <div className="border-t border-[#E4E4E7] pt-2">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">Evidencia</p>
                <p className="text-sm text-[#0A0A0A]">{photos.length} foto{photos.length !== 1 ? 's' : ''}</p>
                {form.descripcion && <p className="text-xs text-[#52525B] mt-1">{form.descripcion}</p>}
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(3)} className="flex-1 h-12 rounded-md border-[#E4E4E7]">
                Atras
              </Button>
              <Button
                data-testid="submit-gestion-btn"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-md font-bold"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {loading ? 'Enviando...' : 'Levantar Gestion'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
